import { createContext, ReactNode, useEffect, useMemo, useReducer, useState } from "react";
import mcpSlice, { initialState, setTeammate, Teammate } from "./mcpSlice";
import { MCPStateProps } from "./mcpSlice";
import { UnknownAction } from "@reduxjs/toolkit";
import { useParams } from "react-router-dom";
import useTeammates from "./useTeammates";
import { Alert, AlertDescription, AlertTitle } from "@altimateai/lego";
import DatamateNotFound from "../teammate/DatamateNotFound";

interface ContextProps {
  state: MCPStateProps;
  dispatch: React.Dispatch<UnknownAction>;
}

export const MCPContext = createContext<ContextProps>({
  state: initialState,
  dispatch: () => null,
});

const MCPProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [state, dispatch] = useReducer(mcpSlice.reducer, mcpSlice.getInitialState());
  const params = useParams();
  const { data: teammatesFromServer, error, isError, isPending } = useTeammates();
  const [teammateNotFound, setTeammateNotFound] = useState(false);

  useEffect(() => {
    if (!params?.teammateId || !teammatesFromServer) {
      return;
    }
    const teammate = teammatesFromServer.find(
      (teammate: Teammate) => teammate.id?.toString() === params.teammateId
    );
    if (teammate) {
      dispatch(setTeammate(teammate));
      setTeammateNotFound(false);
    } else {
      setTeammateNotFound(true);
    }
  }, [params?.teammateId, teammatesFromServer]);

  const values = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state]
  );

  if (isError) {
    return (
      <Alert variant="destructive" role="alert">
        <AlertTitle>Error loading datamates</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : "Failed to load datamates data"}
        </AlertDescription>
      </Alert>
    );
  }

  if (teammateNotFound && !isPending) {
    return <DatamateNotFound />;
  }

  return <MCPContext.Provider value={values}>{children}</MCPContext.Provider>;
};

export default MCPProvider;
